// Google OAuth (direct) — white-label, no Emergent intermediate screen.
// Started 2026-04-25 to replace the auth.emergentagent.com redirect.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function isNativeApp() {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch (_e) {
    return false;
  }
}

function buildGoogleUrl(state) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error('Google sign-in is not configured (REACT_APP_GOOGLE_CLIENT_ID is missing).');
  }
  const redirectUri = `${window.location.origin}/auth/google`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'online',
    include_granted_scopes: 'true',
    prompt: 'select_account',
  });
  if (state) params.set('state', state);
  return `${GOOGLE_AUTH_URL}?${params.toString()}`;
}

/**
 * Web browsers: navigate to Google's consent screen (page unloads; the
 * /auth/google callback finishes the login).
 */
export function startGoogleSignIn() {
  window.location.href = buildGoogleUrl();
}

/**
 * Universal entry point.
 * - Web: identical to startGoogleSignIn(), returns null (page navigates away).
 * - Native app (Capacitor): Google blocks OAuth inside WebViews, so the flow
 *   completes in the system browser. We create a one-time link code, pass it
 *   through OAuth `state`, and poll until the browser-side callback deposits
 *   the JWT — then resolve with { access_token, user } so the app can log
 *   itself in. TV-style device sign-in: no App Links, no Play review needed.
 */
export async function signInWithGoogle() {
  if (!isNativeApp()) {
    window.location.href = buildGoogleUrl();
    return null;
  }

  _cancelFlag = { cancelled: false };
  const flag = _cancelFlag;

  const startRes = await fetch(`${API_URL}/api/auth/google/device-link/start`, { method: 'POST' });
  if (!startRes.ok) throw new Error('Could not start Google sign-in. Please try again.');
  const { link_code: linkCode } = await startRes.json();

  const url = buildGoogleUrl(`dl_${linkCode}`);
  // Top-level navigation to a non-allowed origin: Capacitor opens the system
  // browser and the WebView stays on this page, so polling keeps running.
  const win = window.open(url, '_blank');
  if (!win) window.location.href = url;

  const startedAt = Date.now();
  while (Date.now() - startedAt < 5 * 60 * 1000) {
    await new Promise((r) => setTimeout(r, 2500));
    if (flag.cancelled) throw new Error('GOOGLE_SIGNIN_CANCELLED');
    try {
      const poll = await fetch(`${API_URL}/api/auth/google/device-link/${linkCode}`);
      if (!poll.ok) continue;
      const data = await poll.json();
      if (data.status === 'complete') return data;
      if (data.status === 'expired') break;
    } catch (_e) {
      // transient network hiccup — keep polling
    }
  }
  throw new Error('Google sign-in timed out. Please try again.');
}

// Cancels an in-flight native device-link poll (QA suggestion: give the user
// a way out of the "waiting for the browser" state).
let _cancelFlag = { cancelled: false };
export function cancelGoogleSignIn() {
  _cancelFlag.cancelled = true;
}

/** Role-based landing route after any successful login. */
export function routeForUser(userData) {
  if (userData?.role === 'admin' || userData?.role === 'sub_admin') return '/admin';
  if (userData?.role === 'school_admin' || userData?.role === 'teacher') return '/school';
  if (userData?.role === 'corporate_admin' || userData?.role === 'manager') return '/corporate';
  if (userData?.role === 'parent') return '/parent-dashboard';
  if (userData?.school_id) return '/school/student';
  if (userData?.org_id) return '/corporate/member';
  return '/dashboard';
}
